/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { resolve } from '../../../api/endPoints';

import axiosClient from '../../../utils/axios';

import { ALL } from '../../../config/constants';
export interface TextBlockProps {
  data: {
    name: string;
    language: string;
    category: string;
  } | null;
}

const initialState: any = {
  currentCursorPositions: {} as Record<string, number>,
  copiedDeltaText: {} as Record<string, any>,
  textBlockModal: {
    toggle: false,
    source: '',
    platform: ALL
  },

  data: [],
  loadingAllFolders: false,
  allTextBlockFolders: [],
  loadingCaptions: false,
  textBlockCaptions: [],
  textBlockCount: null,
  nextCaptions: null,
  currentEditorInstance: null,
  textBlock_CreatorInit: false,
  current_platform: null
};

const slice = createSlice({
  name: 'textBlocks',
  initialState,
  reducers: {
    toggleTemplateModal: (state, action: PayloadAction<boolean>) => {
      state.openTemplateModal = action.payload;
    },
    setTextBlockModalUi: (
      state,
      action: PayloadAction<{
        attribute: string;
        value: any;
      }>
    ) => {
      const { attribute, value } = action.payload;

      state.textBlockModal[attribute] = value;
    },
    updateFolderLoadingState: (state, action: PayloadAction<boolean>) => {
      state.loadingAllFolders = action.payload;
    },
    updateAllFolders: (state, action: PayloadAction<any[]>) => {
      state.allTextBlockFolders = action.payload;
    },
    deleteFolder: (state, action: PayloadAction<string>) => {
      state.allTextBlockFolders = state.allTextBlockFolders?.filter(
        (item) => item.uuid !== action.payload
      );
    },
    addFolder: (state, action: PayloadAction<any>) => {
      state.allTextBlockFolders = [
        action.payload,
        ...state.allTextBlockFolders
      ];
    },
    addCaption: (state, action: PayloadAction<any>) => {
      state.textBlockCaptions = [action.payload, ...state.textBlockCaptions];
    },
    updateCaptionLoadingState: (state, action: PayloadAction<boolean>) => {
      state.loadingCaptions = action.payload;
    },
    loadCaptions: (
      state,
      action: PayloadAction<{ captions: any[]; count: number }>
    ) => {
      state.textBlockCaptions = action.payload.captions;
      state.textBlockCount = action.payload.count;
    },
    updateCaptions: (state, action: PayloadAction<any[]>) => {
      state.textBlockCaptions = [...state.textBlockCaptions, ...action.payload];
    },
    updateNextCaptionsUrl: (state, action: PayloadAction<string | null>) => {
      state.nextCaptions = action.payload;
    },
    setTextEditorInstance: (state, action: PayloadAction<any>) => {
      state.currentEditorInstance = action.payload;
    },
    setCurrentPlatform: (state, action: PayloadAction<any>) => {
      state.current_platform = action.payload;
    },
    triggerCreatorQuillInit: (state) => {
      state.textBlock_CreatorInit = !state.textBlock_CreatorInit;
    },
    updateEditedCaption: (state, action: PayloadAction<any>) => {
      const updatedList = state.textBlockCaptions?.map((item) =>
        item.uuid === action.payload?.uuid
          ? { ...item, ...action.payload }
          : item
      );

      state.textBlockCaptions = updatedList;
    },
    deleteCaption: (state, action: PayloadAction<string>) => {
      state.textBlockCaptions = state.textBlockCaptions?.filter(
        (item) => item.uuid !== action.payload
      );
    },
    setCursorPosition: (
      state,
      action: PayloadAction<{ platform: string; index: number }>
    ) => {
      const { platform, index } = action.payload;
      state.currentCursorPositions[platform] = index;
    },
    setCopiedCaption: (
      state,
      action: PayloadAction<{ platform: string; text: any }>
    ) => {
      const { platform, text } = action.payload;
      state.copiedDeltaText[platform] = text;
    }
  }
});

export const {
  toggleTemplateModal,
  setTextBlockModalUi,
  updateFolderLoadingState,
  updateAllFolders,
  deleteFolder,
  addCaption,
  updateCaptionLoadingState,
  loadCaptions,
  updateCaptions,
  updateNextCaptionsUrl,
  setTextEditorInstance,
  triggerCreatorQuillInit,
  setCurrentPlatform,
  updateEditedCaption,
  deleteCaption,
  setCursorPosition,
  setCopiedCaption
} = slice.actions;

export default slice.reducer;

export const getTextBlocksFolder =
  ({
    locationUUID,
    cloudUUID,
    onError,
    search,
    reload = false,
    saved_text_blocks_folder = true
  }: {
    locationUUID: string;
    cloudUUID: string;
    onError?: (err: any) => void;
    search?: string;
    reload?: boolean;
    saved_text_blocks_folder?: boolean;
  }) =>
  async (dispatch: any, getState: any) => {
    const url = resolve(
      'get_text_block_folders',
      {},
      {
        search: search || undefined,
        location__uuid: locationUUID,
        cloud_uuid: cloudUUID,
        parent__isnull: true,
        saved_text_blocks_folder: saved_text_blocks_folder,
        is_multipost: !locationUUID && !cloudUUID
      }
    );

    !reload && dispatch(slice.actions.updateFolderLoadingState(true)); // Start loading

    try {
      // Fetch initial data
      let results: any[] = [];
      const res = await axiosClient.get(url);

      const resultsData = res.data.results;
      results.push(...resultsData);

      // Fetch additional pages if available
      let next = res.data?.next;
      while (next) {
        const pageRes = await axiosClient.get(next);
        const pageResultsData = pageRes.data.results;
        results.push(...pageResultsData);
        next = pageRes.data?.next;
      }

      // Sort items by type and name

      dispatch(slice.actions.updateAllFolders(results));
    } catch (error: any) {
      console.error('Error fetching WhatsApp templates:', error);
      onError?.(error);
      // Dispatch an error action if needed
    } finally {
      !reload && dispatch(slice.actions.updateFolderLoadingState(false)); // Stop loading
    }
  };

export const createTextBlockFolder =
  (
    locationUUID,
    cloudUUID,
    folderInfo,
    onSuccess?: () => void,
    onError?: (err: any) => void,
    saved_text_blocks_folder = true
  ) =>
  async (dispatch: any, getState: any) => {
    const url = resolve(
      'get_text_block_folders',
      {},
      {
        location__uuid: !folderInfo.uuid ? locationUUID : null,
        cloud__uuid: !folderInfo.uuid ? cloudUUID : null,
        is_multipost: !locationUUID && !cloudUUID,
        saved_text_blocks_folder: !folderInfo.uuid
          ? saved_text_blocks_folder
          : null,
        parent: folderInfo.uuid
      }
    );

    // dispatch(slice.actions.updateFolderLoadingState(true)); // Start loading

    try {
      const payload = {
        name: folderInfo.name
      };

      if (folderInfo.uuid) {
        payload['parent_uuid'] = folderInfo.uuid;
      }

      if (locationUUID) {
        payload['location_uuid'] = folderInfo.location_uuid;
      }

      if (cloudUUID) {
        payload['cloud_uuid'] = cloudUUID;
      }

      if (!cloudUUID && !locationUUID) {
        payload['is_multipost'] = true;
      }

      // Send request
      const res = await axiosClient.post(url, payload);
      if (res.data) {
        // Add the new folder to the state instead of reloading all folders
        dispatch(slice.actions.addFolder(res.data));

        onSuccess?.();
      }

    } catch (error: any) {
      console.error('Error fetching WhatsApp templates:', error);
      onError?.(error);

      // Dispatch an error action if needed
    } finally {
      // dispatch(slice.actions.updateFolderLoadingState(false)); // Stop loading
    }
  };

export const editTextBlockFolder =
  (
    locationUUID,
    cloudUUID,
    folderUUID,
    folderInfo,
    onSuccess?: () => void,
    onError?: (err: any) => void,
    saved_text_blocks_folder = true
  ) =>
  async (dispatch: any, getState: any) => {
    const url = resolve(
      'get_single_text_block_folder',
      { folderUUID },
      { location__uuid: locationUUID }
    );

    // dispatch(slice.actions.updateFolderLoadingState(true)); // Start loading

    try {
      // send formdata to endpoint data
      const formData = new FormData();
      formData.append('name', folderInfo.name);

      // Send request
      const res = await axiosClient.patch(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (res.data) {
        onSuccess?.();
        dispatch(
          getTextBlocksFolder({
            locationUUID: locationUUID,
            cloudUUID: cloudUUID,
            reload: true
          })
        );
      }
    } catch (error: any) {
      onError?.(error);
      console.error('Error in folder:', error);

      // Dispatch an error action if needed
    } finally {
      // dispatch(slice.actions.updateFolderLoadingState(false)); // Stop loading
    }
  };

export const deleteTextBlockFolder =
  (
    locationUUID,
    cloudUUID,
    folderUUID,
    onSuccess?: () => void,
    onError?: (err: any) => void
  ) =>
  async (dispatch: any, getState: any) => {
    const url = resolve('get_single_text_block_folder', { folderUUID }, {});

    // dispatch(slice.actions.updateFolderLoadingState(true)); // Start loading

    try {
      // send formdata to endpoint data

      // Send request

      const res = await axiosClient.delete(url, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (res.status === 204) {
        onSuccess?.();

        dispatch(deleteFolder(folderUUID));

        dispatch(
          getTextBlocksFolder({
            locationUUID: locationUUID,
            cloudUUID: cloudUUID,
            reload: true
          })
        );
      }
    } catch (error: any) {
      onError?.(error);
      console.error('Error fetching captions:', error);

      // Dispatch an error action if needed
    } finally {
      // dispatch(slice.actions.updateFolderLoadingState(false)); // Stop loading
    }
  };

export const fetchASubFolder =
  (parentUuid: string, saved_text_blocks_folder = true) =>
  async (_dispatch: any, getState: any): Promise<any[]> => {
    const initialUrl = resolve(
      'get_text_block_folders',
      {},
      {
        parent: parentUuid,
        saved_text_blocks_folder
      }
    );

    const allResults: any[] = [];

    let nextUrl: string | null = initialUrl;

    try {
      while (nextUrl) {
        const res = await axiosClient.get(nextUrl);

        if (res?.data?.results?.length) {
          allResults.push(...res.data.results);
        }

        nextUrl = res.data.next; // null if done
      }

      return allResults;
    } catch (error: any) {
      console.error('Error fetching subfolders:', error);
      return []; // Return empty array on failure
    }
  };

export const getTextBlockCaptions =
  ({
    locationUUID,
    cloudUUID,
    search,
    folderUUID,
    folderIsNull,
    onError,
    onSuccess
  }: {
    locationUUID: string;
    cloudUUID: string;
    search?: string;
    folderUUID?: string;
    folderIsNull?: boolean;
    onError?: (err: any) => void;
    onSuccess?: () => void;
  }) =>
  async (dispatch: any, getState: any) => {
    const url = resolve(
      'get_text_block_captions',
      {},
      {
        search: search || undefined,
        location__uuid: locationUUID || null,
        cloud_uuid: cloudUUID || null,
        folder__uuid: folderUUID || undefined,
        folder__isnull: folderIsNull || undefined,
        is_multipost: !locationUUID && !cloudUUID
      }
    );

    !search && dispatch(slice.actions.updateCaptionLoadingState(true)); // Start loading

    try {
      // Fetch initial data
      let results: any[] = [];
      const res = await axiosClient.get(url);

      const resultsData = res.data.results;
      results.push(...resultsData);

      // Fetch additional pages if available
      let next = res.data?.next;

      dispatch(slice.actions.updateNextCaptionsUrl(next));

      // Sort items by type and name

      dispatch(
        slice.actions.loadCaptions({
          captions: results,
          count: res.data.count
        })
      );

      // Call onSuccess callback if provided
      onSuccess?.();
    } catch (error: any) {
      console.error('Error fetching Captions:', error);
      onError?.(error);
      // Dispatch an error action if needed
    } finally {
      !search && dispatch(slice.actions.updateCaptionLoadingState(false)); // Stop loading
    }
  };
export const getMoreCaptions =
  (nextUrl) => async (dispatch: any, getState: any) => {
    const url = nextUrl;

    // dispatch(slice.actions.updateCaptionLoadingState(true)); // Start loading

    try {
      // Fetch initial data
      let results: any[] = [];
      const res = await axiosClient.get(url);

      const resultsData = res.data.results;
      results.push(...resultsData);

      // Fetch additional pages if available
      let next = res.data?.next;

      dispatch(slice.actions.updateNextCaptionsUrl(next));

      // Sort items by type and name

      dispatch(slice.actions.updateCaptions(results));
    } catch (error: any) {
      console.error('Error fetching Captions:', error);

      // Dispatch an error action if needed
    } finally {
      // dispatch(slice.actions.updateCaptionLoadingState(false)); // Stop loading
    }
  };

export const createTextCaption =
  (
    locationUUID,
    cloudUUID,
    captionInfo,
    onSuccess?: () => void,
    onError?: (err: any) => void,
    folderUUID?
  ) =>
  async (dispatch: any, getState: any) => {
    const url = resolve(
      'get_text_block_captions',
      {},
      {
        location__uuid: !captionInfo.uuid ? locationUUID : null,
        cloud_uuid: !captionInfo.uuid ? cloudUUID : null,
        folder__uuid: folderUUID ? folderUUID : null,
        parent: captionInfo.uuid
      }
    );

    // dispatch(slice.actions.updateFolderLoadingState(true)); // Start loading

    try {
      const payload = {
        title: captionInfo.title,
        text_quill: captionInfo.text_quill
      };

      if (folderUUID) {
        payload['folder'] = folderUUID;
      }

      if (locationUUID) {
        payload['location'] = locationUUID;
      }

      if (cloudUUID) {
        payload['cloud'] = cloudUUID;
      }

      if (!cloudUUID && !locationUUID) {
        payload['is_multipost'] = true;
      }

      const res = await axiosClient.post(url, payload);

      if (res.data) {
        onSuccess?.();
        dispatch(addCaption(res.data));
      }
    } catch (error: any) {
      onError?.(error);
      console.error('Error creating captions:', error);

      // Dispatch an error action if needed
    } finally {
      // dispatch(slice.actions.updateFolderLoadingState(false)); // Stop loading
    }
  };

export const editTextCaption =
  (
    locationUUID,
    cloudUUID,
    captionInfo,
    onSuccess?: () => void,
    onError?: (err: any) => void
  ) =>
  async (dispatch: any, getState: any) => {
    const url = resolve(
      'get_single_text_block_captions',
      { captionUUID: captionInfo?.uuid },
      {
        location__uuid: !captionInfo.uuid ? locationUUID : undefined,
        cloud_uuid: !captionInfo.uuid ? cloudUUID : undefined
      }
    );

    // dispatch(slice.actions.updateFolderLoadingState(true)); // Start loading

    try {
      // send formdata to endpoint data
      const formData = new FormData();
      // formData.append('uuid', folderInfo.uuid);

      formData.append('title', captionInfo.title);
      formData.append('text_quill', JSON.stringify(captionInfo.text_quill));
      if (captionInfo.text_as_html) {
        formData.append('text_as_html', captionInfo.text_as_html);
      }

      // Send request

      const res = await axiosClient.patch(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (res.data) {
        let parsedTextQuill = res.data?.text_quill;

        if (typeof parsedTextQuill === 'string') {
          try {
            parsedTextQuill = JSON.parse(parsedTextQuill);
          } catch (_err) {
            // fall back to the raw string if parsing fails
          }
        }

        const updatedCaption = {
          ...res.data,
          // ensure we keep the uuid used in the list in case the backend omits it
          uuid: res.data?.uuid ?? captionInfo?.uuid,
          // normalise text_quill so the UI gets the latest delta object
          text_quill: parsedTextQuill
          // keep latest html preview for card rendering
        };

        dispatch(updateEditedCaption(updatedCaption));
        onSuccess?.();
      }
    } catch (error: any) {
      onError?.(error);
      console.error('Error fetching captions:', error);

      // Dispatch an error action if needed
    } finally {
      // dispatch(slice.actions.updateFolderLoadingState(false)); // Stop loading
    }
  };

export const deleteTextCaption =
  (
    locationUUID,
    cloudUUID,
    captionInfo,
    onSuccess?: () => void,
    onError?: (err: any) => void
  ) =>
  async (dispatch: any, getState: any) => {
    const url = resolve(
      'get_single_text_block_captions',
      { captionUUID: captionInfo?.uuid },
      {}
    );

    // dispatch(slice.actions.updateFolderLoadingState(true)); // Start loading

    try {
      // send formdata to endpoint data

      // Send request

      const res = await axiosClient.delete(url, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (res.status >= 200 && res.status < 300) {
        dispatch(deleteCaption(captionInfo?.uuid));
        onSuccess?.();
      }
    } catch (error: any) {
      onError?.(error);
      console.error('Error fetching captions:', error);

      // Dispatch an error action if needed
    } finally {
      // dispatch(slice.actions.updateFolderLoadingState(false)); // Stop loading
    }
  };

export const moveTextCaptionToFolder =
  (
    locationUUID,
    cloudUUID,
    captionUUID,
    folderUUID,
    onSuccess?: () => void,
    onError?: (err: any) => void
  ) =>
  async (dispatch: any, getState: any) => {
    const url = resolve('get_single_text_block_captions', { captionUUID }, {});

    try {
      // Get the current state to check the textblock's current folder
      const state = getState();
      const textblock = state.textBlocks.textBlockCaptions.find(
        (caption) => caption.uuid === captionUUID
      );

      // If the textblock is already in the target folder, don't update it
      if (textblock && textblock.folder === folderUUID) {
        console.log('Textblock is already in this folder, skipping update');
        onSuccess?.();
        return;
      }

      // Create JSON data with the new folder
      const jsonData = {
        folder: folderUUID || null
      };

      // Send request to update the caption's folder
      const res = await axiosClient.patch(url, jsonData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (res.data) {
        onSuccess?.();
        // Remove the item from the current list as it was moved away
        dispatch(deleteCaption(captionUUID));
      }
    } catch (error: any) {
      onError?.(error);
      console.error('Error moving caption to folder:', error);
    }
  };
